import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle } from 'lucide-react';
import { FinancialRiskToolbar } from './FinancialRiskToolbar';
import { FinancialRiskSectionList } from './FinancialRiskSectionList';
import { FinancialRiskFlagsList } from './FinancialRiskFlagsList';
import { FinancialRiskComparisonModal } from './FinancialRiskComparisonModal';
import {
  runFinancialRiskAnalysis,
  fetchFinancialRisks,
  type FinancialRiskFinding,
} from '../../services/api';

interface FinancialRiskAnalysisPanelProps {
  contractId: string;
}

export const FinancialRiskAnalysisPanel: React.FC<FinancialRiskAnalysisPanelProps> = ({
  contractId,
}) => {
  const [findings, setFindings] = useState<FinancialRiskFinding[]>([]);
  const [selectedSectionKey, setSelectedSectionKey] = useState<string | null>(null);
  const [filters, setFilters] = useState({ severity: '', flagType: '', keyword: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [comparisonTarget, setComparisonTarget] = useState<{
    finding: FinancialRiskFinding;
    isOpen: boolean;
  } | null>(null);

  // Load existing findings on mount
  useEffect(() => {
    const loadFindings = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await fetchFinancialRisks(contractId);
        setFindings(
          result.findings.map((f: any) => ({
            id: f.id,
            clauseId: f.clause_id || f.clauseId,
            clauseText: f.clause_text || f.clauseText,
            articleNumber: f.article_number || f.articleNumber,
            sectionNumber: f.section_number || f.sectionNumber,
            sectionTitle: f.section_title || f.sectionTitle || '',
            ruleId: f.rule_id || f.ruleId,
            ruleName: f.rule_name || f.ruleName,
            flagType: f.flag_type || f.flagType,
            severity: f.severity,
            matchText: f.match_text || f.matchText,
            charOffsetStart: f.char_offset_start ?? f.charOffsetStart,
            charOffsetEnd: f.char_offset_end ?? f.charOffsetEnd,
            rationale: f.rationale,
          }))
        );
      } catch (err) {
        // No existing findings is fine — not an error
        console.log('[FinancialRisk] No existing findings:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadFindings();
  }, [contractId]);

  const handleRunAnalysis = useCallback(async () => {
    setIsRunning(true);
    setError(null);
    try {
      const result = await runFinancialRiskAnalysis(contractId);
      setFindings(
        result.findings.map((f: any) => ({
          id: f.id,
          clauseId: f.clauseId,
          clauseText: f.clauseText,
          articleNumber: f.articleNumber,
          sectionNumber: f.sectionNumber,
          sectionTitle: f.sectionTitle || '',
          ruleId: f.ruleId,
          ruleName: f.ruleName,
          flagType: f.flagType,
          severity: f.severity,
          matchText: f.matchText,
          charOffsetStart: f.charOffsetStart,
          charOffsetEnd: f.charOffsetEnd,
          rationale: f.rationale,
        }))
      );
      setSelectedSectionKey(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Analysis failed');
    } finally {
      setIsRunning(false);
    }
  }, [contractId]);

  const handleCompare = useCallback((finding: FinancialRiskFinding) => {
    setComparisonTarget({ finding, isOpen: true });
  }, []);

  // Apply client-side filters
  const filteredFindings = findings.filter((f) => {
    if (filters.severity && f.severity !== filters.severity) return false;
    if (filters.flagType && f.flagType !== filters.flagType) return false;
    if (
      filters.keyword &&
      !f.clauseText.toLowerCase().includes(filters.keyword.toLowerCase())
    )
      return false;
    return true;
  });

  return (
    <div
      className="bg-white rounded-lg shadow-md"
      data-testid="financial-risk-panel"
    >
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center gap-2 mb-3">
          <div className="p-2 rounded-lg text-red-600 bg-red-100">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">
            Financial Risk Analysis
          </h2>
          {findings.length > 0 && (
            <span className="text-sm text-gray-500">
              {findings.length} finding{findings.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        <FinancialRiskToolbar
          filters={filters}
          onFilterChange={setFilters}
          onRunAnalysis={handleRunAnalysis}
          isRunning={isRunning}
          hasFindings={findings.length > 0}
        />
      </div>

      {/* Error */}
      {error && (
        <div className="px-6 py-3 bg-red-50 text-red-700 text-sm border-b border-red-100">
          {error}
        </div>
      )}

      {/* Content */}
      <div className="p-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin h-8 w-8 border-4 border-primary-600 border-t-transparent rounded-full" />
          </div>
        ) : filteredFindings.length === 0 ? (
          <div className="text-center py-12" data-testid="no-findings">
            <AlertTriangle className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">
              {findings.length === 0
                ? 'No Analysis Run Yet'
                : 'No Findings Match Filters'}
            </h3>
            <p className="text-sm text-gray-500">
              {findings.length === 0
                ? 'Click "Run Analysis" to scan this contract for financial risk indicators.'
                : 'Try adjusting your filters to see more results.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left column — sections */}
            <div className="lg:col-span-1">
              <FinancialRiskSectionList
                findings={filteredFindings}
                selectedSectionKey={selectedSectionKey}
                onSelectSection={setSelectedSectionKey}
              />
            </div>
            {/* Right column — flags */}
            <div className="lg:col-span-2">
              <FinancialRiskFlagsList
                findings={filteredFindings}
                selectedSectionKey={selectedSectionKey}
                onCompare={handleCompare}
              />
            </div>
          </div>
        )}
      </div>

      {/* Comparison modal */}
      {comparisonTarget && (
        <FinancialRiskComparisonModal
          finding={comparisonTarget.finding}
          sourceContractId={contractId}
          isOpen={comparisonTarget.isOpen}
          onClose={() => setComparisonTarget(null)}
        />
      )}
    </div>
  );
};
